import { supabase, dbWrite } from './supabase';

// Public half of the VAPID key pair; the private half is a Supabase secret.
// Per deployment, like the brand settings. Unset means reminders stay hidden.
export const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';

export function registerServiceWorker() {
  if (!VAPID_PUBLIC_KEY || !('serviceWorker' in navigator)) return;
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(err => console.error('Service worker failed:', err));
  });
}

const isIos = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches || navigator.standalone === true;

// What this device can do: 'ready', 'denied', 'ios-install' (an iPhone only
// gets push once the app is on the home screen), or 'unsupported'.
export function pushSupport() {
  if (isIos() && !isStandalone()) return 'ios-install';
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return 'unsupported';
  if (window.Notification.permission === 'denied') return 'denied';
  return 'ready';
}

function urlBase64ToUint8Array(base64) {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(padded);
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

async function currentSubscription() {
  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.getSubscription();
}

export async function deviceSubscribed() {
  if (pushSupport() !== 'ready' || window.Notification.permission !== 'granted') return false;
  return !!(await currentSubscription());
}

// Must run from a tap: iOS and Safari only show the permission prompt then.
export async function subscribeDevice(clientId) {
  const permission = await window.Notification.requestPermission();
  if (permission !== 'granted') throw new Error('Notifications were not allowed on this device.');
  const reg = await navigator.serviceWorker.ready;
  const sub = (await reg.pushManager.getSubscription()) || await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
  });
  const { endpoint, keys } = sub.toJSON();
  // A shared device may still carry a subscription row from someone else's
  // account; that row isn't ours to delete, so start this browser afresh.
  const { error } = await supabase.from('push_subscriptions').insert({
    client_id: clientId, endpoint, p256dh: keys.p256dh, auth: keys.auth, user_agent: navigator.userAgent.slice(0, 200),
  });
  if (error && error.code === '23505') {
    const mine = await supabase.from('push_subscriptions').select('id').eq('endpoint', endpoint).maybeSingle();
    if (mine.error) throw new Error(mine.error.message);
    if (!mine.data) {
      await sub.unsubscribe();
      return subscribeDevice(clientId);
    }
  } else if (error) {
    throw new Error(error.message);
  }
}

export async function unsubscribeDevice() {
  const sub = await currentSubscription();
  if (!sub) return;
  await dbWrite(supabase.from('push_subscriptions').delete().eq('endpoint', sub.endpoint), 'remove device');
  await sub.unsubscribe();
}

// Called on a deliberate sign-out, so a shared device stops showing this
// person's reminders to whoever signs in next. getRegistration rather than
// serviceWorker.ready: ready never settles on a device that never registered
// the worker, and sign-out must not wait on it.
export async function releaseDevice() {
  if (!('serviceWorker' in navigator)) return;
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = reg && await reg.pushManager?.getSubscription();
  if (!sub) return;
  // Unsubscribing is what matters. If the row delete fails, the push service
  // answers 410 on the next send and send-reminders removes the row itself.
  const { error } = await supabase.from('push_subscriptions').delete().eq('endpoint', sub.endpoint);
  if (error) console.error('Could not remove this device from reminders:', error.message);
  await sub.unsubscribe();
}

export async function sendTestReminder() {
  const { data, error } = await supabase.functions.invoke('send-reminders', { body: { test: true } });
  if (error) throw new Error(error.message);
  return data?.sent ?? 0;
}
